import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { API } from '../App'; // Removed useAuth
import { 
  ArrowRight, Bot, BookOpen, Star, Users, Video, FileText, 
  FileSpreadsheet, Image, Beaker, Calculator, Library
} from 'lucide-react';

const Home = () => {
  const [settings, setSettings] = useState(null);
  const [featuredContent, setFeaturedContent] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, contentRes, reviewsRes] = await Promise.all([
          axios.get(`${API}/settings`),
          axios.get(`${API}/content`),
          axios.get(`${API}/reviews`),
        ]);
        setSettings(settingsRes.data);
        setFeaturedContent(contentRes.data.slice(0, 6));
        setReviews(reviewsRes.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const stats = [
    { label: 'Students', value: '10K+', icon: Users },
    { label: 'Courses', value: '500+', icon: BookOpen },
    { label: 'Reviews', value: '4.9', icon: Star },
  ];

  return (
    <div className="pt-16" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${settings?.hero_image_url || 'https://images.pexels.com/photos/30547584/pexels-photo-30547584.jpeg'})`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/80 via-[#050505]/70 to-[#050505]"></div>
        </div>

        {/* Grid Pattern */}
        <div className="absolute inset-0 grid-pattern opacity-30"></div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="animate-fadeIn">
            <span className="inline-block px-4 py-1 mb-6 text-xs font-semibold tracking-[0.2em] uppercase text-red-500 border border-red-500/30 rounded-sm bg-red-500/10">
              Journey Towards Excellence
            </span>
            <h1 className="font-outfit text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter text-white mb-6">
              STUDY<span className="text-red-500">.</span>POINT
            </h1>
            <p className="text-xl md:text-2xl text-zinc-400 max-w-2xl mx-auto mb-10 font-jakarta">
              {settings?.hero_subtitle || 'Empowering students from class 6th to 12th with quality education and AI-powered learning'}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/classes">
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg rounded-sm"
                  data-testid="explore-classes-btn"
                >
                  Explore Classes
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/dauter-ai">
                <Button
                  variant="outline"
                  className="border-zinc-700 text-white hover:bg-zinc-800 px-8 py-6 text-lg rounded-sm"
                  data-testid="try-ai-btn"
                >
                  <Bot className="mr-2 w-5 h-5" />
                  Try Dauter AI
                </Button>
              </Link>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-20 grid grid-cols-3 gap-8 max-w-2xl mx-auto animate-fadeIn stagger-2">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <stat.icon className="w-8 h-8 text-red-500 mx-auto mb-2" />
                <div className="font-outfit text-3xl md:text-4xl font-bold text-white">{stat.value}</div>
                <div className="text-sm text-zinc-500 uppercase tracking-wide">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Classes Preview */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-12">
            <div>
              <span className="text-sm uppercase tracking-[0.2em] font-semibold text-zinc-500">Education</span>
              <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mt-2">
                Browse <span className="text-red-500">Classes</span>
              </h2>
            </div>
            <Link to="/classes" className="text-red-500 hover:text-red-400 flex items-center gap-2">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {['6th', '7th', '8th', '9th', '10th', '11th', '12th'].map((level, idx) => (
              <Link
                key={level}
                to={`/classes/${level.replace('th', '')}`}
                className={`card-hover bg-[#121212] p-6 text-center rounded-sm animate-fadeIn stagger-${idx + 1}`}
                data-testid={`class-${level}-card`}
              >
                <div className="font-outfit text-3xl font-bold text-white mb-1">{level}</div>
                <div className="text-xs text-zinc-500 uppercase tracking-wide">Class</div>
              </Link>
            ))}
          </div>

          {/* Streams for 11th & 12th */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { id: 'commerce', name: 'Commerce', Icon: Calculator },
              { id: 'science', name: 'Science', Icon: Beaker },
              { id: 'biology', name: 'Biology', Icon: Library },
              { id: 'humanities', name: 'Humanities', Icon: BookOpen },
            ].map((stream, idx) => (
              <Link
                key={stream.id}
                to={`/classes/11/${stream.id}`}
                className={`card-hover bg-[#121212] p-6 rounded-sm flex items-center gap-4 animate-fadeIn stagger-${idx + 1}`}
                data-testid={`stream-${stream.id}-card`}
              >
                <div className="w-12 h-12 rounded-sm bg-red-500/10 flex items-center justify-center">
                  <stream.Icon className="w-6 h-6 text-red-500" />
                </div>
                <div>
                  <div className="font-semibold text-white">{stream.name}</div>
                  <div className="text-xs text-zinc-500">11th & 12th</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Dauter AI Section */}
      <section className="py-20 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm uppercase tracking-[0.2em] font-semibold text-red-500">AI Assistant</span>
              <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mt-2 mb-6">
                Meet <span className="text-red-500">Dauter AI</span>
              </h2>
              <p className="text-zinc-400 mb-8 leading-relaxed">
                Your intelligent study companion powered by GPT-5.2. Get instant help with homework, 
                exam preparation, and career guidance. Available 24/7 to answer all your academic questions.
              </p>
              <Link to="/dauter-ai">
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 rounded-sm pulse-red"
                  data-testid="chat-now-btn"
                >
                  <Bot className="mr-2 w-5 h-5" />
                  Chat Now
                </Button>
              </Link>
            </div>
            <div className="chat-container rounded-sm p-6">
              <div className="space-y-4">
                <div className="chat-message-user p-4 rounded-sm">
                  <p className="text-sm text-zinc-300">Can you help me understand photosynthesis?</p>
                </div>
                <div className="chat-message-ai p-4 rounded-sm">
                  <p className="text-sm text-zinc-300">
                    Of course! Photosynthesis is the process by which plants convert sunlight, water, 
                    and carbon dioxide into glucose and oxygen. Let me break it down for you...
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Content */}
      {featuredContent.length > 0 && (
        <section className="py-20 bg-[#050505]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-end mb-12">
              <div>
                <span className="text-sm uppercase tracking-[0.2em] font-semibold text-zinc-500">Resources</span>
                <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mt-2">
                  Featured <span className="text-red-500">Content</span>
                </h2>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredContent.map((content) => {
                const getIcon = (type) => {
                  switch(type) {
                    case 'video': return Video;
                    case 'pdf': return FileText;
                    case 'ppt': return FileSpreadsheet;
                    case 'image': return Image;
                    default: return FileText;
                  }
                };
                const ContentIcon = getIcon(content.content_type);
                
                return (
                  <Link
                    key={content.content_id}
                    to={`/content/${content.content_id}`}
                    className="card-hover bg-[#121212] rounded-sm overflow-hidden"
                    data-testid={`content-${content.content_id}`}
                  >
                    <div className="h-40 bg-gradient-to-br from-red-600/20 to-zinc-800 flex items-center justify-center">
                      <ContentIcon className="w-12 h-12 text-red-500" />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs px-2 py-1 bg-red-500/10 text-red-500 rounded-sm uppercase flex items-center gap-1">
                          <ContentIcon className="w-3 h-3" />
                          {content.content_type}
                        </span>
                        {content.is_paid && (
                          <span className="text-xs px-2 py-1 bg-yellow-500/10 text-yellow-500 rounded-sm">
                            Paid
                          </span>
                        )}
                      </div>
                      <h3 className="font-semibold text-white mb-1">{content.title}</h3>
                      <p className="text-sm text-zinc-500 line-clamp-2">{content.description}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Reviews */}
      {reviews.length > 0 && (
        <section className="py-20 bg-[#0A0A0A]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="text-sm uppercase tracking-[0.2em] font-semibold text-zinc-500">Testimonials</span>
              <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mt-2">
                What Students <span className="text-red-500">Say</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {reviews.map((review) => (
                <div
                  key={review.review_id}
                  className="card-hover bg-[#121212] p-6 rounded-sm"
                >
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${i < review.rating ? 'text-yellow-500 fill-yellow-500' : 'text-zinc-600'}`}
                      />
                    ))}
                  </div>
                  <p className="text-zinc-400 mb-4">{review.comment}</p>
                  <div className="text-sm font-semibold text-white">{review.user_name}</div>
                </div>
              ))}
            </div>

            <div className="text-center mt-8">
              <Link to="/reviews">
                <Button variant="outline" className="border-zinc-700 text-white hover:bg-zinc-800 rounded-sm">
                  View All Reviews
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA - Now links directly to classes without asking for login */}
      <section className="py-20 bg-gradient-to-b from-[#050505] to-red-950/20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Start Your <span className="text-red-500">Journey</span>?
          </h2>
          <p className="text-zinc-400 mb-8">
            Join thousands of students who are already learning with STUDY POINT. 
            Explore all our resources and educational materials completely free.
          </p>
          <Link to="/classes">
            <Button
              className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg rounded-sm"
              data-testid="cta-explore-btn"
            >
              Start Exploring
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
